import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import axios from "axios";
import moment from "moment/moment";
import momentTz from "moment-timezone";
import {
  styleReleaseDate,
  formatEventTimeForViewer,
  getEventTimezoneLabel,
} from "../helpers";
import {
  ValidatorForm,
  TextValidator,
  SelectValidator,
} from "react-material-ui-form-validator";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CssBaseline,
  Box,
  Checkbox,
  Link,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import MuiAlert from "@material-ui/lab/Alert";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import {
  GDPR_TEXT,
  PRIVACY_POLICY_LINK_TEXT,
  TERMS_AND_CONDITIONS_LINK_TEXT,
  PRIVACY_POLICY_LINK_URL,
  TERMS_AND_CONDITIONS_LINK_URL,
} from "../config/config";
import WebcastingContext from "../context/webcasting/webcastingContext";
import RegisteredUserContext from "../context/registeredUser/registeredUserContext";
import { DS_SMITH_EVENT } from "../components/eventSpecific/dsSmith";
import TateLyleTitle from "../components/eventSpecific/tateLyle";

// HELPERS
import { gaEvent } from "../helpers/reactGA";
import { useLocation } from "react-router-dom";

// COMPONENTS
import HeadStyleForNoReg from "./HeadStyleForNoReg";
import Spinner from "../components/UI/Spinner";

import "./index.css";
import "./RegistrationEmail.css";

const useStyles = makeStyles((theme) => ({
  buttonv1: {
    color: "#fff",
    backgroundColor: "#4caf50f2",
    "&:hover": {
      color: "#fff",
      backgroundColor: "#4caf50",
    },
  },
  formRoot: {
    padding: "0.5rem",
  },
  date: {
    margin: "1.7rem 0px",
    color: "var(--clr-g-7e)",
    [theme.breakpoints.down("xs")]: {
      margin: "1.7rem 0px",
    },
    "& span": {
      fontSize: "1rem",
    },
  },
  mainContent: {
    padding: "8px 24px",
    marginBottom: "1.5rem",
    [theme.breakpoints.down("xs")]: {
      marginBottom: "0.85rem",
    },
  },
  contentText: {
    marginTop: "20px",
    marginBottom: "0px",
    fontSize: "0.8rem",
  },
  input: {
    "& ::before": {
      borderBottom: "2px solid #b3b3b3",
    },
    "& ::after": {
      borderBottom: "2px solid #cccccc !important",
    },
    "& :hover::before": {
      borderBottom: "2px solid #7e7e7e !important",
    },
  },
  footer: {
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 24px 12px",
    fontSize: "11px",
    "& div": {
      "& span": {
        padding: "0px",
      },
    },
    "& p": {
      margin: "0px 0.2rem",
      lineHeight: 1.3,
      fontSize: "11px",
    },
    "& a": {
      color: "#333",
      fontWeight: "700",
      lineHeight: 1.3,
      textDecoration: "underline",
    },
    "& button": {
      color: "#333",
      backgroundColor: "#fff",
      "&:hover": {
        color: "#fff",
        backgroundColor: "#333",
      },
    },
  },
  footerOnlyBtn: {
    justifyContent: "flex-end",
    padding: "6px 24px 12px",
    "& button": {
      color: "#333",
      backgroundColor: "#fff",
      "&:hover": {
        color: "#fff",
        backgroundColor: "#333",
      },
    },
  },
}));

/** Parse field.values string (e.g. "UK, USA") into array of trimmed options. */
function parseFieldValues(field) {
  if (!field || typeof field !== "object") return [];
  const raw = field.values;
  if (typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const RegistrationEmail = (props) => {
  const classes = useStyles();
  const { search, pathname } = useLocation();

  const event = props.event;
  const isDsSmithEvent = event?._id === DS_SMITH_EVENT.id;
  const isRenishawEvent = event?._id === "6968c43021449c0013b7966c";
  const isTateLyleEvent = event?._id === "6ad8939fe50bec7a2ac8fa23";
  const eventId = props.match?.match?.params?.eventId;

  const webcastingContext = useContext(WebcastingContext);
  const registeredUserContext = useContext(RegisteredUserContext);

  const {
    registrant,
    setRegistrant,
    localData,
    saveLocalData,
    branding,
  } = webcastingContext;
  const { getUserRegistation, userRegistration } = registeredUserContext;

  const [email, setEmail] = useState("");
  const [autoFillName, setAutoFillName] = useState("");
  const [autoFillCompany, setAutoFillCompany] = useState("");

  const [submitReady, setSubmitReady] = useState(false);
  const [formData, setFormData] = useState({});
  const [registrationData, setRegistrationData] = useState({});

  const [disabledEmail, setDisabledEmail] = useState(true);
  const [disabledData, setDisabledData] = useState(true);
  const [submittedEmail, setSubmittedEmail] = useState(false);
  const [submittedData, setSubmittedData] = useState(false);
  const [buttonEmailText, setButtonEmailText] = useState("Next");
  const [buttonDataText, setButtonDataText] = useState("Log in");

  const [emailAuthorised, setEmailAuthorised] = useState(true);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);

  const [checkedPrivacy, setCheckedPrivacy] = useState(false);
  const [checkedDisclaimer, setCheckedDisclaimer] = useState(false);
  const [registrationFields, setRegistrationFields] = useState([]);
  const [showPsw, setShowPsw] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const [userReadyIntermediaries, setUserReadyIntermediaries] = useState(false);
  const [userDisagree, setUserDisagree] = useState(false);

  const saveLocalAuthCalledRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const lastCheckedEmailRef = useRef(null);

  // Derived from event (no state)
  const newDateFormat = useMemo(() => {
    if (!event?.releaseTime) return null;
    return styleReleaseDate(
      formatEventTimeForViewer(event.releaseTime, event.dateFormat || ""),
      event.releaseTime
    );
  }, [event?.releaseTime, event?.dateFormat]);

  const timeZone = useMemo(() => {
    if (!event?.releaseTime) return null;
    return getEventTimezoneLabel(event.releaseTime);
  }, [event?.releaseTime]);
  const disclaimer = Boolean(event?.disclaimerRequired);
  const disclaimerText = event?.disclaimerBody ?? "";

  const hasRegistrationFields =
    event?.registrationRequired && registrationFields.length > 0;

  const handleIntermediariesAgree = () => setUserReadyIntermediaries(true);
  const handleIntermediariesDisagree = () => setUserDisagree(true);

  // Debounced check of email restrictions while typing
  const DEBOUNCE_MS = 500;
  useEffect(() => {
    if (!eventId) return;
    const emailVal = (registrationData.email || "").trim();
    const isValidEmail = /\S+@\S+\.\S+/.test(emailVal);

    if (!emailVal || !isValidEmail) {
      setEmailAuthorised(true);
      setEmailCheckLoading(false);
      lastCheckedEmailRef.current = null;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      debounceTimerRef.current = null;
      lastCheckedEmailRef.current = emailVal;
      setEmailCheckLoading(true);
      setErrorMsg("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/public/api/event/checkEmailRestrictions`,
          { eventId, email: emailVal }
        );
        const access = res.data?.data?.access === true || res.data?.access === true;
        if (lastCheckedEmailRef.current === emailVal) {
          setEmailAuthorised(access);
        }
      } catch (err) {
        if (lastCheckedEmailRef.current === emailVal) {
          setEmailAuthorised(false);
          setErrorMsg(
            err.response?.data?.error ||
              (typeof err.response?.data === "string" ? err.response.data : null) ||
              err.message ||
              "Error checking email restrictions"
          );
        }
      } finally {
        if (lastCheckedEmailRef.current === emailVal) {
          setEmailCheckLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [eventId, registrationData.email]);

  // Google Analytics + basic GA event tags
  useEffect(() => {
    if (!event) return;
    if (eventId !== "6617f9d68d18254c64eac445") setUserReadyIntermediaries(true);

    gaEvent("Event", "Show", eventId);
    gaEvent("company_name", "view", event.companies?.[0]?.name ?? "");
    gaEvent("event_title", "view", event.title ?? "");
    gaEvent("company_code", "view", event.companies?.[0]?.collection_code ?? "");
    gaEvent(event.isLive ? "live" : "onDemand", "active", new Date().toISOString());
  }, [event, eventId]);

  // Initial registration fields + meetmax auto-fill
  useEffect(() => {
    if (!event) return;

    const meetmaxParams = new URLSearchParams(search).get("meetmax");
    const meetmaxName = new URLSearchParams(search).get("name");
    const meetmaxEmail = new URLSearchParams(search).get("email");
    const meetmaxCompany = new URLSearchParams(search).get("company");

    if (event.registrationRequired) {
      const fields =
        event.registrationFields?.filter((field) => {
          if (typeof field === "string")
            return field.toLowerCase() !== "email";
          const fieldType = field.type?.toLowerCase();
          const fieldKey = field.key?.toLowerCase();
          return fieldType !== "email" && fieldKey !== "email";
        }) ?? [];
      setRegistrationFields(fields);

      if (fields.length > 0) {
        const initialData = {};
        fields.forEach((field) => {
          if (typeof field === "object" && field.key && field.value) {
            const key = field.key.toLowerCase().trim();
            initialData[key] = field.value;
          }
        });
        if (Object.keys(initialData).length > 0) {
          setRegistrationData((prev) => ({ ...prev, ...initialData }));
        }
      }
    }

    if (meetmaxParams === "true") {
      setEmail(meetmaxEmail || "");
      setAutoFillName(meetmaxName || "");
      setAutoFillCompany(meetmaxCompany || "");
    }
  }, [event, search]);

  // Meetmax: auto-fill registrationData from URL
  useEffect(() => {
    if (new URLSearchParams(search).get("meetmax") === "true") {
      setRegistrationData({
        email,
        name: autoFillName,
        organisation: autoFillCompany,
      });
    }
  }, [email, autoFillCompany, autoFillName, search]);

  // Keep formData in sync with registrationData
  useEffect(() => {
    setFormData({
      eventid: eventId,
      fields: registrationData,
    });
  }, [registrationData, eventId]);

  const handleCheckboxPrivacy = (e) => {
    setCheckedPrivacy(e.target.checked);
  };

  const handleCheckboxDisclaimer = (e) => {
    setCheckedDisclaimer(e.target.checked);
  };

  const handleChangeEmail = (e) => {
    const { name, value } = e.target;
    setEmail(value);
    setRegistrationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeData = (e) => {
    const { name, value } = e.target;
    setRegistrationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckExistingEmail = () => {
    setButtonEmailText("sending...");
    localStorage.setItem("wpEmail", registrationData.email);
    getUserRegistation(eventId, registrationData.email);
  };

  const handleChangeSelect = (name) => (e) => {
    const value = e.target.value;
    setRegistrationData((prev) => ({ ...prev, [name]: value }));
  };

  /** Single choice: store one value in registrationData[name]. */
  const handleChangeRadio = (name) => (e) => {
    const value = e.target.value;
    setRegistrationData((prev) => ({ ...prev, [name]: value }));
  };

  /** Toggle a checkbox option; stores comma-separated selected values in registrationData[name]. */
  const handleChangeCheckbox = (name, optionValue) => (e) => {
    const checked = e.target.checked;
    setRegistrationData((prev) => {
      const current = (prev[name] || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const next = checked
        ? [...current, optionValue]
        : current.filter((v) => v !== optionValue);
      return { ...prev, [name]: next.join(",") };
    });
  };

  const handleSubmitEmail = () => {
    if (!emailAuthorised) return;
    setButtonEmailText("sending...");
    setSubmittedEmail(true);
    if (registrationFields.length === 0) {
      setSubmittedData(true);
      setButtonDataText("sending...");
      setFormData({ eventid: eventId, fields: registrationData });
      setSubmitReady(true);
    } else {
      handleCheckExistingEmail();
    }
  };

  useEffect(() => {
    if (!openAlert) return;
    const t = setTimeout(() => {
      setButtonEmailText("Next");
      setEmailAuthorised(true);
      setOpenAlert(false);
    }, 5000);
    return () => clearTimeout(t);
  }, [openAlert]);

  const handleBack = () => {
    setSubmittedEmail(false);
    setButtonEmailText("Next");
  };

  const handleSubmitData = async (e) => {
    e.preventDefault();
    setSubmittedData(true);
    setButtonDataText("sending...");
    setFormData({ eventid: eventId, fields: registrationData });

    gaEvent(
      "signup",
      "submit",
      `signup successfully with name: ${registrationData.name} and email: ${registrationData.email}`
    );

    // Hard-coded password gates on specific events
    if (
      typeof registrationData.password !== "undefined" &&
      registrationData.password !== undefined
    ) {
      const pwd = registrationData.password;

      const checkPasswordFail = (msg) => {
        setErrorMsg(msg);
        setSubmittedData(false);
        setDisabledData(true);
        setSubmitReady(false);
        setButtonDataText("LOG IN");
      };

      if (event._id === "650980df50a62b392af1791d") {
        if (pwd !== "it123") return checkPasswordFail("Incorrect password");
      }

      if (event._id === "64db32182b85fd91126d7185") {
        if (pwd !== "cvs2023!") return checkPasswordFail("Incorrect password");
      }

      if (event._id === "67e6a4a2a8f35502be8e07be") {
        if (pwd !== "z0tef0amsCMD25!!") return checkPasswordFail("Incorrect password");
      }
    }

    if (event.agmCheck) {
      const srn =
        registrationData.srn ||
        registrationData.investorid ||
        registrationData["shareholder reference number"];
        
      const pin = registrationData.pin;

      const setAgmFail = (msg) => {
        setErrorMsg(msg || "Invalid SRN or PIN");
        setSubmittedData(false);
        setDisabledData(true);
        setSubmitReady(false);
        setButtonDataText("LOG IN");
      };

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/public/api/event/checkAgmSRNAndPIN`,
          {
            srn: srn != null ? String(srn).trim() : "",
            pin: pin != null ? String(pin).trim() : "",
            eventID: eventId,
          }
        );

        console.log('resresresresresres', res.data)  

        if (res.data?.success === true) {
          setSubmitReady(true);
        } else {
          setAgmFail(res.data?.error?.error);
        }
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          (typeof err.response?.data === "string" ? err.response.data : null) ||
          err.message;
        setAgmFail(msg);
      }
    } else if (!registrationData.password) {
      setSubmitReady(true);
    }
  };

  const handleSubmitDataForMeetMax = (e) => {
    e.preventDefault();
    setSubmittedData(true);
    setSubmitReady(true);
    setButtonDataText("sending...");

    gaEvent(
      "signup",
      "submit",
      `signup successfully with name: ${registrationData.name} and email: ${registrationData.email}`
    );
  };

  useEffect(() => {

    if (submitReady) 
      setRegistrant(eventId, formData);
  }, [submitReady, formData, eventId, setRegistrant]);

  // Handle userRegistration response (login or signup path)
  useEffect(() => {
    if (!userRegistration || Object.keys(userRegistration).length === 0) return;

    if (
      userRegistration?.data?.findId !== undefined &&
      userRegistration?.data?.findId !== null
    ) {
      const reg = userRegistration;
      const fieldsObj = JSON.parse(reg.data.fields);
      setEmail(fieldsObj.email);
      setFormData({
        eventid: reg.data.eventId,
        fields: fieldsObj,
        findId: reg.data.findId,
        _id: reg.data._id,
      });
      gaEvent("login", "submit", `login successfully with email: ${fieldsObj.email}`);
      localStorage.removeItem("wpEmail");
      setIsRegistered(true);
      return;
    }

    if (userRegistration?.success === false) {
      const emailFromStorage = localStorage.getItem("wpEmail") || "";
      setEmail(emailFromStorage);
      setRegistrationData((prev) => ({ ...prev, email: emailFromStorage }));

      if (registrationFields.length === 0) {
        setSubmittedData(true);
        setButtonDataText("sending...");
        setFormData({ eventid: eventId, fields: registrationData });
        setSubmitReady(true);
      }
      setSubmittedEmail(true);
    }
  }, [userRegistration]);

  const validatorListenerEmail = (result) => {
    if (email.length < 1) {
      setDisabledEmail(true);
    } else {
      setDisabledEmail(!result);
    }
  };

  function validateEmailSimple(value) {
    const re = /\S+@\S+\.\S+/;
    return re.test(value);
  }

  const validatorListenerData = () => {
    const fields = Object.entries(registrationData);
    const emailVal = registrationData.email;
    if (!emailVal || !validateEmailSimple(emailVal)) {
      setDisabledData(true);
      return;
    }

    const anyEmpty = fields.some(([, v]) => !v || String(v).length < 1);
    const passwordEventIds = [
      "64db32182b85fd91126d7185",
      "650980df50a62b392af1791d",
      "67e6a4a2a8f35502be8e07be",
    ];
    const needsPassword =
      passwordEventIds.includes(event?._id) &&
      (!registrationData.password || registrationData.password.length === 0);
    setDisabledData(anyEmpty || needsPassword);
  };

  const saveLocalAuthAndGo = () => {
    if (saveLocalAuthCalledRef.current) return;
    saveLocalAuthCalledRef.current = true;

    const payload = {
      eventId,
      auth: true,
      findId: Object.keys(formData).length ? formData.findId : registrant?.findId,
      registrant: Object.keys(formData).length ? formData.fields : registrant,
      _id: Object.keys(formData).length ? formData._id : registrant?._id,
    };

    const events = localData.user.events || [];
    const idx = events.findIndex((ev) => ev.eventId === eventId);
    const nextEvents = [...events];
    if (idx >= 0) {
      nextEvents[idx] = { ...nextEvents[idx], ...payload };
    } else {
      nextEvents.push(payload);
    }
    saveLocalData({ ...localData, user: { ...localData.user, events: nextEvents } });

    const idToUse = isRegistered ? formData.findId : registrant?.findId;
    window.location.href = `../../broadcast/${eventId}/${idToUse}`;
  };

  const handleCloseSnack = (_, reason) => {
    if (reason === "clickaway") return;
    if (errorMsg) {
      setErrorMsg("");
      setDisabledData(false);
    }
    setButtonEmailText("Next");
    setEmailAuthorised(true);
  };

  const handleTermsUseLink = () => {
    if (!new URLSearchParams(search).has("previewKey") && !pathname.includes("preview")) {
      gaEvent("login", "click", "terms of use link");
    }
  };

  const handlePrivacyLink = () => {
    if (!new URLSearchParams(search).has("previewKey") && !pathname.includes("preview")) {
      gaEvent("login", "click", "privacy link");
    }
  };

  const isIntermediariesEvent = eventId === "6617f9d68d18254c64eac445";

  const isMeetmax = new URLSearchParams(search).get("meetmax") === "true";

  return (
    <>
      {branding ? <HeadStyleForNoReg branding={branding} /> : <HeadStyleForNoReg />}

      <CssBaseline />

      {/* Intermediaries special disclaimer gate */}
      {isIntermediariesEvent && !userDisagree && !userReadyIntermediaries && (
        <Box m={2} p={1}>
          {/* Long disclaimer block kept as-is, just using current event shape */}
          {/* ... (unchanged disclaimer text) ... */}
          <Typography
            className="title"
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            Registration for the presentation to Intermediaries on Thursday 18th April at
            11.30 am
          </Typography>
          <br />
          <Typography
            className="title"
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            IMPORTANT NOTICE
          </Typography>
          {/* ... rest of disclaimer text omitted here for brevity, keep identical ... */}
          {/* (You can keep your original disclaimer JSX as-is here) */}

          <Box style={{ display: "flex", flexDirection: "row", marginTop: "1rem" }}>
            <Button
              color="secondary"
              onClick={handleIntermediariesDisagree}
              style={{ margin: "auto" }}
              variant="contained"
            >
              Disagree
            </Button>

            <Button
              onClick={handleIntermediariesAgree}
              className={classes.buttonv1}
              style={{ margin: "auto" }}
              variant="contained"
            >
              Agree
            </Button>
          </Box>
        </Box>
      )}

      {userDisagree && (
        <Box m={2} p={1}>
          <Box style={{ display: "flex", justifyContent: "center" }}>
            <CancelIcon
              style={{
                margin: "auto",
                textAlign: "center",
                fontSize: "100px",
                color: "#f50057",
              }}
            />
          </Box>
          <br />
          <Typography
            className="title"
            style={{ textAlign: "center", fontWeight: "" }}
          >
            Due to legal restrictions you are not allowed to register for the presentation
          </Typography>
        </Box>
      )}

      {userReadyIntermediaries && (
        <ValidatorForm
          onSubmit={handleSubmitEmail}
          onError={(errors) => console.log(errors)}
          className={classes.formRoot}
        >
          {branding ? (
            <div className={classes.mainContent}>
              <p className="title branding-title">
                <TateLyleTitle eventId={event._id}>
                  {event._id === "69175e8f27ca94001446d2f9"
                    ? "Analyst Presentation"
                    : event._id === "68f6124464cab60013721242"
                    ? "Investor Presentation"
                    : event.title}
                </TateLyleTitle>
              </p>

              {newDateFormat && timeZone && (
                <Typography
                  variant="h6"
                  component="h6"
                  className="title"
                  style={{ textAlign: "center" }}
                >
                  {isDsSmithEvent
                    ? DS_SMITH_EVENT.dateLabel
                    : event._id === "68e908cfcfea8e0013596700"
                    ? "Wednesday, November 19, 2025, 12:00 PM (UK Time)"
                    : formatEventTimeForViewer(event.releaseTime, `${event.dateFormat} `)}
                  {!isDsSmithEvent && event._id !== "68e908cfcfea8e0013596700" && (
                    <span>({timeZone})</span>
                  )}
                </Typography>
              )}

              {event._id === "6617f9d68d18254c64eac445" && (
                <h2 className="title branding-title">
                  The presentation will contain information that must remain confidential
                </h2>
              )}
            </div>
          ) : (
            <>
              <DialogTitle
                id="form-dialog-title"
                className="registration-title"
              >
                <TateLyleTitle eventId={event._id}>{event.title}</TateLyleTitle>
              </DialogTitle>
              {isDsSmithEvent && (
                <Typography variant="h6" component="h6" style={{ textAlign: "center" }}>
                  {DS_SMITH_EVENT.dateLabel}
                </Typography>
              )}
              {event._id === "6617f9d68d18254c64eac445" && (
                <DialogTitle
                  id="form-dialog-title"
                  className="registration-title"
                >
                  The presentation will contain information that must remain confidential
                </DialogTitle>
              )}
            </>
          )}

          {isTateLyleEvent && (
            <DialogContent>
              <Typography className="description">
                Please note that this registration page is for the presentation only. The conference call details for the 9:00am Management Q&amp;A can be found on the next page.
              </Typography>
            </DialogContent>
          )}

          {/* Error alerts */}
          <div style={{ width: "100%" }}>
            {!emailAuthorised && (
              <Alert severity="error">
                Your email is not authorised to watch this event
              </Alert>
            )}
            {errorMsg !== "" && (
              <Alert severity="error" onClose={handleCloseSnack}>
                {errorMsg}
              </Alert>
            )}
          </div>

          {/* STEP 1: EMAIL */}
          {!submittedEmail && !submittedData ? (
            <>
              <DialogContent className="email-input">
                <DialogContentText
                  className={`content-text ${classes.contentText}`}
                >
                  Please enter your email address
                </DialogContentText>

                <TextValidator
                  className="description"
                  autoFocus
                  margin="dense"
                  id="email"
                  fullWidth
                  label="Email Address"
                  onChange={handleChangeEmail}
                  name="email"
                  value={email}
                  validators={["required", "isEmail"]}
                  errorMessages={[
                    "this field is required",
                    "email is not valid",
                  ]}
                  validatorListener={validatorListenerEmail}
                  disabled={submittedEmail}
                />
              </DialogContent>

              <DialogActions
                className={`footer ${classes.footer}`}
              >
                <div className="footer__content">
                    {isRenishawEvent && (
                      <>
                        <p>This event is being operated by BRR Media and presented by Renishaw plc.</p>
                        <br />
                      </>
                    )}
                    <div>
                      <Checkbox
                        checked={checkedPrivacy}
                        onChange={handleCheckboxPrivacy}
                        inputProps={{ "aria-label": "terms of use checkbox" }}
                      />
                      <Typography vairant="body2" className="">
                        {isRenishawEvent ? "I have read and accepted the BRR Media " : GDPR_TEXT}
                        <Link
                          onClick={handleTermsUseLink}
                          href={TERMS_AND_CONDITIONS_LINK_URL}
                          target="_blank"
                        >
                          {isRenishawEvent ? "Terms of Use" : TERMS_AND_CONDITIONS_LINK_TEXT}
                        </Link>
                        {isRenishawEvent ? " and " : "and "}
                        <Link
                          onClick={handlePrivacyLink}
                          href={PRIVACY_POLICY_LINK_URL}
                          target="_blank"
                        >
                          {isRenishawEvent ? "Privacy Policy" : PRIVACY_POLICY_LINK_TEXT}
                        </Link>
                        {isRenishawEvent && (
                          <>
                            {" and the Renishaw "}
                            <Link
                              href="https://www.renishaw.com/legal/en/privacy-notice--43453"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Notice.
                            </Link>
                          </>
                        )}
                      </Typography>
                    </div>
                    {disclaimer && (
                      <div>
                        <Checkbox
                          checked={checkedDisclaimer}
                          onChange={handleCheckboxDisclaimer}
                          inputProps={{ "aria-label": "disclaimer checkbox" }}
                        />
                        <Typography className="description">
                          I have read and accepted the <span>disclaimer</span> down below
                        </Typography>
                      </div>
                    )}
                  </div>

                <Button
                  variant="contained"
                  onClick={handleSubmitEmail}
                  disabled={
                    !disclaimer
                      ? !checkedPrivacy || disabledEmail || submittedEmail || emailCheckLoading || !emailAuthorised
                      : !checkedPrivacy ||
                        !checkedDisclaimer ||
                        disabledEmail ||
                        submittedEmail ||
                        emailCheckLoading ||
                        !emailAuthorised
                  }
                >
                  {emailCheckLoading ? "Checking..." : buttonEmailText}
                </Button>
              </DialogActions>

              <DialogContent>
                <Typography className="">
                  On registering for this event, analytical cookies will be stored on your
                  browser. If you do not wish that to happen, then you should not proceed.
                </Typography>
              </DialogContent>

              {disclaimer && (
                <DialogContent className="disclaimer-content">
                  <h3>Disclaimer</h3>
                  <div
                    dangerouslySetInnerHTML={{ __html: disclaimerText }}
                  ></div>
                </DialogContent>
              )}
            </>
          ) : submittedEmail && !submittedData && hasRegistrationFields ? (
            // STEP 3: ADDITIONAL FIELDS (if registrationFields exist and are not empty)
            <>
              <DialogContent>
                <DialogContentText
                  className={`content-text ${classes.contentText}`}
                >
                  Please fill out the details below
                </DialogContentText>

                {registrationFields
                  .filter((field) => {
                    // Double-check: skip email fields (should already be filtered, but safety check)
                    if (typeof field === "string") {
                      return field.toLowerCase().trim() !== "email";
                    }
                    const fieldType = field.type?.toLowerCase();
                    const fieldKey = field.key?.toLowerCase()?.trim();
                    return fieldType !== "email" && fieldKey !== "email";
                  })
                  .map((field, idx) => {
                    // Handle both old (string) and new (object) structure
                    const fieldKey =
                      typeof field === "string" ? field : field.key;
                    const fieldType =
                      typeof field === "string" ? "text" : field.type;
                    const fieldTypeLower = fieldType?.toLowerCase?.() || "text";
                    const fieldName =
                      (typeof fieldKey === "string"
                        ? fieldKey.toLowerCase().trim()
                        : "") || "";

                    // Select: dropdown from field.values
                    if (fieldTypeLower === "select") {
                      const options = parseFieldValues(field);
                      if (options.length === 0) {
                        if (process.env.NODE_ENV !== "production") {
                          console.warn(
                            "Registration field type 'select' has no values:",
                            field
                          );
                        }
                        return (
                          <TextValidator
                            className={classes.input}
                            key={
                              typeof field === "object" ? field._id || idx : idx
                            }
                            autoFocus={idx === 0}
                            margin="dense"
                            fullWidth
                            label={fieldKey}
                            onChange={handleChangeData}
                            name={fieldName}
                            type="text"
                            value={registrationData[fieldName] || ""}
                            validators={["required", "minStringLength:1"]}
                            errorMessages={[
                              `${fieldKey} is required`,
                              "this field is required",
                            ]}
                            validatorListener={validatorListenerData}
                            disabled={submittedData}
                          />
                        );
                      }
                      return (
                        <Box key={typeof field === "object" ? field._id || idx : idx} margin="dense" mt={1} mb={1}>
                          <SelectValidator
                            className={classes.input}
                            name={fieldName}
                            value={registrationData[fieldName] || ""}
                            onChange={handleChangeSelect(fieldName)}
                            fullWidth
                            validators={["required", "minStringLength:1"]}
                            errorMessages={[
                              `${fieldKey} is required`,
                              "this field is required",
                            ]}
                            validatorListener={validatorListenerData}
                            SelectProps={{ displayEmpty: true }}
                            disabled={submittedData}
                          >
                            <MenuItem value="">
                              <em>Select {fieldKey}</em>
                            </MenuItem>
                            {options.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </SelectValidator>
                        </Box>
                      );
                    }

                    // Radio (single choice): RadioGroup from field.values
                    if (fieldTypeLower === "radio") {
                      const options = parseFieldValues(field);
                      if (options.length === 0) {
                        if (process.env.NODE_ENV !== "production") {
                          console.warn(
                            "Registration field type 'radio' has no values:",
                            field
                          );
                        }
                        return (
                          <TextValidator
                            className={classes.input}
                            key={
                              typeof field === "object" ? field._id || idx : idx
                            }
                            autoFocus={idx === 0}
                            margin="dense"
                            fullWidth
                            label={fieldKey}
                            onChange={handleChangeData}
                            name={fieldName}
                            type="text"
                            value={registrationData[fieldName] || ""}
                            validators={["required", "minStringLength:1"]}
                            errorMessages={[
                              `${fieldKey} is required`,
                              "this field is required",
                            ]}
                            validatorListener={validatorListenerData}
                            disabled={submittedData}
                          />
                        );
                      }
                      return (
                        <FormControl
                          key={
                            typeof field === "object" ? field._id || idx : idx
                          }
                          component="fieldset"
                          margin="dense"
                          fullWidth
                          style={{ marginTop: 8, marginBottom: 8 }}
                        >
                          <FormLabel component="legend" style={{ marginBottom: 4 }}>
                            {fieldKey}
                          </FormLabel>
                          <RadioGroup
                            name={fieldName}
                            value={registrationData[fieldName] || ""}
                            onChange={handleChangeRadio(fieldName)}
                            row
                          >
                            {options.map((opt) => (
                              <FormControlLabel
                                key={opt}
                                value={opt}
                                control={<Radio color="primary" />}
                                label={opt}
                                disabled={submittedData}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      );
                    }

                    // Checkbox (multi-select): checkboxes from field.values
                    if (fieldTypeLower === "checkbox") {
                      const options = parseFieldValues(field);
                      if (options.length === 0) {
                        if (process.env.NODE_ENV !== "production") {
                          console.warn(
                            "Registration field type 'checkbox' has no values:",
                            field
                          );
                        }
                        return (
                          <TextValidator
                            className={classes.input}
                            key={
                              typeof field === "object" ? field._id || idx : idx
                            }
                            autoFocus={idx === 0}
                            margin="dense"
                            fullWidth
                            label={fieldKey}
                            onChange={handleChangeData}
                            name={fieldName}
                            type="text"
                            value={registrationData[fieldName] || ""}
                            validators={["required", "minStringLength:1"]}
                            errorMessages={[
                              `${fieldKey} is required`,
                              "this field is required",
                            ]}
                            validatorListener={validatorListenerData}
                            disabled={submittedData}
                          />
                        );
                      }
                      const selected = (registrationData[fieldName] || "")
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      return (
                        <FormControl
                          key={
                            typeof field === "object" ? field._id || idx : idx
                          }
                          component="fieldset"
                          margin="dense"
                          fullWidth
                          required
                          style={{ marginTop: 8, marginBottom: 8 }}
                        >
                          <Box display="flex" flexWrap="wrap" flexDirection="row">
                            {options.map((opt) => (
                              <FormControlLabel
                                key={opt}
                                control={
                                  <Checkbox
                                    color="primary"
                                    checked={selected.includes(opt)}
                                    onChange={handleChangeCheckbox(
                                      fieldName,
                                      opt
                                    )}
                                    disabled={submittedData}
                                  />
                                }
                                label={opt}
                              />
                            ))}
                          </Box>
                        </FormControl>
                      );
                    }

                    // Default: text / password
                    return (
                      <TextValidator
                        className={classes.input}
                        key={
                          typeof field === "object" ? field._id || idx : idx
                        }
                        autoFocus={idx === 0}
                        margin="dense"
                        fullWidth
                        label={fieldKey}
                        onChange={handleChangeData}
                        name={fieldName}
                        type={
                          fieldTypeLower === "password" ? "password" : "text"
                        }
                        value={registrationData[fieldName] || ""}
                        validators={["required", "minStringLength:1"]}
                        errorMessages={[
                          `${fieldKey} is required`,
                          "this field is required",
                        ]}
                        validatorListener={validatorListenerData}
                        disabled={submittedData}
                      />
                    );
                  })}

                <TextValidator
                  className={classes.input}
                  margin="dense"
                  fullWidth
                  label="Email Address"
                  onChange={handleChangeEmail}
                  name="email"
                  value={email}
                  validators={["required", "isEmail"]}
                  errorMessages={["this field is required", "email is not valid"]}
                  validatorListener={validatorListenerData}
                  disabled={submittedData}
                />
              </DialogContent>

              <DialogActions className={`footer ${classes.footerOnlyBtn}`}>
                {isMeetmax ? (
                  <Button
                    className="btn-login"
                    variant="contained"
                    onClick={handleSubmitDataForMeetMax}
                  >
                    {buttonDataText}
                  </Button>
                ) : (
                  <Button
                    className="btn-login"
                    variant="contained"
                    onClick={handleSubmitData}
                    disabled={disabledData || submittedData}
                  >
                    {buttonDataText}
                  </Button>
                )}
              </DialogActions>
            </>
          ) : (
            // Fallback: spinner while waiting on userRegistration or registrant context
            <>
              <DialogContent>
                <Box mt={2}>
                  <Spinner />
                </Box>
              </DialogContent>
              <DialogActions>&nbsp;</DialogActions>
            </>
          )}
        </ValidatorForm>
      )}

      {/* When registration succeeds, save auth & redirect */}
      {(registrant?.findId || isRegistered) && <>{saveLocalAuthAndGo()}</>}
    </>
  );
};

export default React.memo(RegistrationEmail);
